import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import Modal from 'react-modal';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './SerialPassFailChart.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import {
    buildPieChartParams,
    fetchAteSwVersions,
    fetchSerialNumbers,
    fetchTotalProduction,
    fetchUutStatusCount,
    fetchUutStatusDetails,
} from './pieChart/pieChartApi';
import {
    buildChartData,
    getActiveFilters,
    getDateFromDaysBack,
    getPieMetrics,
    quickDateOptions,
} from './pieChart/pieChartData';

ChartJS.register(ArcElement, Tooltip, Legend);

const emptyFilters = {
    startDate: null,
    endDate: null,
    ateSwVersion: '',
    selectedSerialNumbers: [],
    selectedFamilies: [],
};

const SerialPassFailChart = () => {
    const [data, setData] = useState({ PASS: 0, FAIL: 0 });
    const [totalProduction, setTotalProduction] = useState(0);
    const [error, setError] = useState(null);
    const [drillDownData, setDrillDownData] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [isTableVisible, setIsTableVisible] = useState(true);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [ateSwVersion, setAteSwVersion] = useState('');
    const [ateSwVersions, setAteSwVersions] = useState([]);
    const [serialNumbers, setSerialNumbers] = useState([]);
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    const [activeDateField, setActiveDateField] = useState(null);
    const chartRef = useRef(null);

    const currentFilters = useMemo(() => ({
        startDate,
        endDate,
        ateSwVersion,
        selectedSerialNumbers,
        selectedFamilies,
    }), [ateSwVersion, endDate, selectedFamilies, selectedSerialNumbers, startDate]);

    const chartData = useMemo(() => buildChartData(data), [data]);
    const metrics = useMemo(() => getPieMetrics(data, totalProduction), [data, totalProduction]);
    const activeFilters = useMemo(() => getActiveFilters(currentFilters), [currentFilters]);
    const hasChartData = (Number(data.PASS) || 0) + (Number(data.FAIL) || 0) > 0;

    const loadChart = useCallback(async (filters) => {
        setError(null);
        setIsFiltering(true);

        try {
            const params = buildPieChartParams(filters);
            const [statusCount, productionTotal] = await Promise.all([
                fetchUutStatusCount(params),
                fetchTotalProduction(params),
            ]);

            setData(statusCount);
            setTotalProduction(productionTotal);
            setDrillDownData([]);
            setSelectedStatus(null);
        } catch (fetchError) {
            console.error('Error fetching pie chart data:', fetchError);
            setError('Failed to load pie chart data. Please try again.');
        } finally {
            setLoading(false);
            setIsFiltering(false);
        }
    }, []);

    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const [versions, serials] = await Promise.all([
                    fetchAteSwVersions(),
                    fetchSerialNumbers(),
                ]);

                setAteSwVersions(versions);
                setSerialNumbers(serials);
            } catch (metadataError) {
                console.error('Error fetching filter metadata:', metadataError);
                setError('Some filter options could not be loaded.');
            }
        };

        loadMetadata();
        loadChart(emptyFilters);
    }, [loadChart]);

    const applyFilters = () => {
        loadChart(currentFilters);
    };

    const resetFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setAteSwVersion('');
        setSelectedSerialNumbers([]);
        setSelectedFamilies([]);
        loadChart(emptyFilters);
    };

    const handleDateSelect = (date) => {
        if (activeDateField === 'start') {
            setStartDate(date);
        } else {
            setEndDate(date);
        }

        setIsDateModalOpen(false);
    };

    const handleChartClick = async (elements) => {
        if (!elements.length) return;

        const status = elements[0].index === 0 ? 'PASS' : 'FAIL';
        setSelectedStatus(status);
        setError(null);

        try {
            const params = {
                ...buildPieChartParams(currentFilters),
                status,
            };
            const details = await fetchUutStatusDetails(params);
            setDrillDownData(details);
            setIsTableVisible(true);
        } catch (fetchError) {
            console.error('Error fetching drill-down data:', fetchError);
            setError('Failed to load drill-down data.');
        }
    };

    const exportChartAsPNG = async () => {
        if (!chartRef.current) return;

        const canvas = await html2canvas(chartRef.current);
        canvas.toBlob((blob) => {
            if (blob) saveAs(blob, 'pie-chart-analysis.png');
        });
    };

    const exportTableAsCSV = () => {
        if (!drillDownData.length) return;

        const csvRows = [['Serial Number', 'Count'], ...drillDownData.map((row) => [row.serialNumber, row.count])];
        const csvContent = csvRows.map((row) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `${selectedStatus || 'status'}-data.csv`);
    };

    const exportTableAsPDF = () => {
        if (!drillDownData.length) return;

        const doc = new jsPDF();
        doc.text(`${selectedStatus || 'Status'} Details`, 10, 10);
        doc.autoTable({
            head: [['Serial Number', 'Count']],
            body: drillDownData.map((row) => [row.serialNumber, row.count]),
            startY: 20,
        });
        doc.save(`${selectedStatus || 'status'}-data.pdf`);
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingScreen}>
                    <div className={styles.spinner}></div>
                    <p>Loading pie chart data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>Production quality</p>
                    <h2 className={styles.title}>Pie Chart Analysis</h2>
                    <p className={styles.description}>
                        Compare pass and fail units, filter by test conditions, then click a chart segment to see the serial numbers behind it.
                    </p>
                </div>
                <button className={styles.helpButtonInline} onClick={() => setIsHelpOpen(true)}>
                    Page help
                </button>
            </header>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <section className={styles.filterPanel} aria-label="Pie chart filters">
                <div className={styles.filterHeader}>
                    <div>
                        <h3>Filters</h3>
                        <p>Choose the date range, software version, and serial numbers to analyze.</p>
                    </div>
                    <button onClick={resetFilters} className={styles.resetButton} disabled={isFiltering}>
                        Reset
                    </button>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.filterItem}>
                        <label>Start date</label>
                        <button
                            className={styles.datePickerButton}
                            onClick={() => {
                                setActiveDateField('start');
                                setIsDateModalOpen(true);
                            }}
                        >
                            {startDate ? startDate.toLocaleDateString() : 'Any start date'}
                        </button>
                    </div>

                    <div className={styles.filterItem}>
                        <label>End date</label>
                        <button
                            className={styles.datePickerButton}
                            onClick={() => {
                                setActiveDateField('end');
                                setIsDateModalOpen(true);
                            }}
                        >
                            {endDate ? endDate.toLocaleDateString() : 'Any end date'}
                        </button>
                    </div>

                    <div className={styles.filterItem}>
                        <label>ATE SW version</label>
                        <select
                            value={ateSwVersion}
                            onChange={(event) => setAteSwVersion(event.target.value)}
                            className={styles.dropdown}
                        >
                            <option value="">All versions</option>
                            {ateSwVersions.map((version) => (
                                <option key={version.value} value={version.value}>
                                    {version.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={`${styles.filterItem} ${styles.serialFilter}`}>
                        <label>Serial numbers</label>
                        <Select
                            isMulti
                            options={serialNumbers}
                            value={selectedSerialNumbers}
                            onChange={(values) => setSelectedSerialNumbers(values || [])}
                            placeholder="All serial numbers"
                            className={styles.multiSelect}
                            classNamePrefix="serialSelect"
                        />
                    </div>
                </div>

                <div className={styles.filterFooter}>
                    <div className={styles.activeFilters}>
                        {activeFilters.length ? (
                            activeFilters.map((filter) => (
                                <span className={styles.filterChip} key={filter}>
                                    {filter}
                                </span>
                            ))
                        ) : (
                            <span className={styles.noFilters}>Showing all available test results</span>
                        )}
                    </div>
                    <button onClick={applyFilters} className={styles.applyButton} disabled={isFiltering}>
                        {isFiltering ? 'Applying...' : 'Apply filters'}
                    </button>
                </div>

                <SourceFileSummary
                    compact
                    startDate={startDate}
                    endDate={endDate}
                    ateSwVersion={ateSwVersion}
                    serialNumbers={selectedSerialNumbers}
                    selectedFamilies={selectedFamilies}
                    onFamiliesChange={setSelectedFamilies}
                    title="Source files used for dashboard charts"
                />
            </section>

            <section className={styles.metricsGrid} aria-label="Pie chart summary">
                {metrics.map((metric) => (
                    <div className={`${styles.metricCard} ${styles[metric.tone]}`} key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                    </div>
                ))}
            </section>

            <main className={styles.analysisGrid}>
                <section className={styles.chartPanel}>
                    <div className={styles.panelHeader}>
                        <div>
                            <h3>Pass / fail split</h3>
                            <p>Click Pass or Fail to inspect the serial numbers in that group.</p>
                        </div>
                    </div>

                    <div className={styles.chartContainer} ref={chartRef}>
                        {hasChartData ? (
                            <Pie
                                data={chartData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            position: 'bottom',
                                        },
                                    },
                                    onClick: (event, elements) => handleChartClick(elements),
                                }}
                            />
                        ) : (
                            <div className={styles.emptyState}>No pass/fail data found for these filters.</div>
                        )}
                    </div>
                </section>

                <section className={styles.actionsPanel}>
                    <h3>Exports</h3>
                    <p>Save the chart or the selected drill-down table.</p>
                    <div className={styles.exportButtons}>
                        <button onClick={exportChartAsPNG} className={styles.exportButton} disabled={!hasChartData}>
                            Chart PNG
                        </button>
                        <button onClick={exportTableAsCSV} className={styles.exportButton} disabled={!drillDownData.length}>
                            Table CSV
                        </button>
                        <button onClick={exportTableAsPDF} className={styles.exportButton} disabled={!drillDownData.length}>
                            Table PDF
                        </button>
                        <button onClick={() => setIsTableVisible((visible) => !visible)} className={styles.exportButton}>
                            {isTableVisible ? 'Hide table' : 'Show table'}
                        </button>
                    </div>
                </section>
            </main>

            {isTableVisible && (
                <section className={styles.drillDownSection}>
                    <div className={styles.panelHeader}>
                        <div>
                            <h3>{selectedStatus ? `${selectedStatus} serial numbers` : 'Drill-down table'}</h3>
                            <p>
                                {selectedStatus
                                    ? 'Sorted by the number of related test records.'
                                    : 'Click a highlighted pass or fail segment to fill this table.'}
                            </p>
                        </div>
                    </div>

                    {drillDownData.length ? (
                        <div className={styles.tableWrap}>
                            <table className={styles.drillDownTable}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Serial number</th>
                                        <th>Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drillDownData.map((row, index) => (
                                        <tr key={`${row.serialNumber}-${row.count}`}>
                                            <td>{index + 1}</td>
                                            <td>{row.serialNumber}</td>
                                            <td>{row.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className={styles.emptyState}>No serial-number group selected yet.</div>
                    )}
                </section>
            )}

            <Modal
                isOpen={isDateModalOpen}
                onRequestClose={() => setIsDateModalOpen(false)}
                className={styles.modalContent}
                overlayClassName={styles.modalOverlay}
                contentLabel="Select Date"
            >
                <h2>Select {activeDateField === 'start' ? 'start' : 'end'} date</h2>

                <div className={styles.quickSelectContainer}>
                    {quickDateOptions.map((option) => (
                        <button
                            key={option.label}
                            onClick={() => handleDateSelect(getDateFromDaysBack(option.daysBack))}
                            className={styles.quickSelectButton}
                        >
                            {option.label}
                        </button>
                    ))}
                    <button onClick={() => handleDateSelect(null)} className={`${styles.quickSelectButton} ${styles.clearButton}`}>
                        Clear
                    </button>
                </div>

                <DatePicker
                    selected={activeDateField === 'start' ? startDate : endDate}
                    onChange={handleDateSelect}
                    inline
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                />

                <div className={styles.modalActions}>
                    <button onClick={() => setIsDateModalOpen(false)} className={styles.closeModalButton}>
                        Cancel
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={isHelpOpen}
                onRequestClose={() => setIsHelpOpen(false)}
                className={styles.helpModalContent}
                overlayClassName={styles.helpModalOverlay}
                contentLabel="Help Information"
            >
                <button onClick={() => setIsHelpOpen(false)} className={styles.closeHelpButton}>
                    Close
                </button>

                <div className={styles.helpText}>
                    <h2>About this page</h2>
                    <p>
                        This page groups tested hardware units by serial number. A unit is marked FAIL if at least one selected test result failed. It is marked PASS only when none of the selected records failed.
                    </p>
                    <p>
                        The filters come from the uploaded test data: test dates, ATE software versions, and unique serial numbers. The drill-down table is loaded after selecting a pie slice.
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default SerialPassFailChart;
